
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Save, X, Camera, Scan, Plus, Minus, ZoomIn, AlertTriangle, FileText } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { BarcodeScanner } from "./BarcodeScanner";
import { AddPhotoButton } from "./AddPhotoButton";
import { QuantityControl } from "./QuantityControl";
import { ImageZoomModal } from "./ImageZoomModal";
import { CollapsibleSection } from "./ui/collapsible-section";
import { CreditRequestForm } from "./credit/CreditRequestForm";
import { ProductFamilyCard } from "./product-family/ProductFamilyCard";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";

interface Product {
  id: string;
  barcode: string;
  name: string;
  boxes: number;
  unitsPerBox: number;
  photos: string[];
  hasCredit: boolean;
}

interface ProductEditProps {
  product: Product;
  orderId: string;
  onBack: () => void;
  onSave: () => void;
}

export const ProductEdit = ({ product, orderId, onBack, onSave }: ProductEditProps) => {
  const [editedProduct, setEditedProduct] = useState<Product>(product);
  const [showScanner, setShowScanner] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [zoomedImage, setZoomedImage] = useState<string | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    setEditedProduct(product);
  }, [product]);

  const handleImageClick = (imageUrl: string) => {
    setZoomedImage(imageUrl);
  };

  const handleScanResult = (scannedBarcode: string) => {
    setEditedProduct(prev => ({ ...prev, barcode: scannedBarcode }));
    toast({
      title: "Barcode Updated",
      description: `Barcode updated to ${scannedBarcode}`,
    });
  };

  const handlePhotoAdded = (photoUrl: string) => {
    setEditedProduct(prev => ({
      ...prev,
      photos: [...prev.photos, photoUrl]
    }));
  };

  const removePhoto = (index: number) => {
    setEditedProduct(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    // Require at least one photo before saving
    if (!editedProduct.photos || editedProduct.photos.length === 0) {
      toast({
        title: "Photo required",
        description: "Please add at least one product photo before saving.",
        variant: "destructive",
      });
      return;
    }

    // Product name is now optional

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('products')
        .update({
          barcode: editedProduct.barcode,
          name: editedProduct.name,
          boxes: editedProduct.boxes,
          units_per_box: editedProduct.unitsPerBox,
          photos: editedProduct.photos,
          updated_at: new Date().toISOString()
        })
        .eq('id', product.id);

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ['products', orderId] });
      
      toast({
        title: "Product Updated",
        description: `${editedProduct.name} has been updated successfully.`,
      });

      onSave();
    } catch (error: any) {
      console.error('Error updating product:', error);
      toast({
        title: "Error",
        description: "Failed to update product. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      <div className="container mx-auto p-4 md:p-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={onBack} size="sm">
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline ml-1">Back</span>
              </Button>
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
                  Edit Product
                  {editedProduct.hasCredit && (
                    <AlertTriangle className="w-5 h-5 text-orange-500" />
                  )}
                </h1>
                <p className="text-sm text-gray-600">#{product.id}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Product Details Form */}
          <Card>
            <CardHeader>
              <CardTitle>Product Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Product Name</label>
                <Input
                  value={editedProduct.name}
                  onChange={(e) => setEditedProduct(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter product name (optional)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Barcode</label>
                <div className="flex gap-2">
                  <Input
                    value={editedProduct.barcode}
                    onChange={(e) => setEditedProduct(prev => ({ ...prev, barcode: e.target.value }))}
                    placeholder="Enter or scan barcode"
                  />
                  <Button onClick={() => setShowScanner(true)} variant="outline" size="sm">
                    <Scan className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Boxes</label>
                  <QuantityControl
                    label="Boxes"
                    value={editedProduct.boxes}
                    onChange={(value) => setEditedProduct(prev => ({ ...prev, boxes: value }))}
                    min={0}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Units per Box</label>
                  <QuantityControl
                    label="Units per Box"
                    value={editedProduct.unitsPerBox}
                    onChange={(value) => setEditedProduct(prev => ({ ...prev, unitsPerBox: value }))}
                    min={1}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Photo Management */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Product Photos
                <AddPhotoButton
                  productId={product.id}
                  productBarcode={editedProduct.barcode}
                  onPhotoAdded={handlePhotoAdded}
                  variant="outline"
                  size="sm"
                />
              </CardTitle>
            </CardHeader>
            <CardContent>
              {editedProduct.photos.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Camera className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No photos added</p>
                  <p className="text-sm">Click "Add Photo" to capture images</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {editedProduct.photos.map((photo, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={photo}
                        alt={`Product photo ${index + 1}`}
                        className="w-full h-32 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => handleImageClick(photo)}
                      />
                      <div className="absolute top-1 right-1 bg-black bg-opacity-50 rounded-full p-1">
                        <ZoomIn className="w-3 h-3 text-white" />
                      </div>
                      <Button
                        onClick={() => removePhoto(index)}
                        variant="destructive"
                        size="sm"
                        className="absolute top-1 left-1 opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 p-0"
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Product Family Section */}
        <div className="mt-6">
          <ProductFamilyCard
            productId={product.id}
            productName={editedProduct.name}
            barcode={editedProduct.barcode}
            showAddButton={true}
          />
        </div>

        {/* Credit Request Section */}
        <div className="mt-6">
          <CollapsibleSection
            title="Request Credit"
            icon={<FileText className="w-5 h-5" />}
            defaultOpen={false}
            className="bg-white"
          >
            <CreditRequestForm
              productId={product.id}
              productName={editedProduct.name}
              orderId={orderId}
              unitPrice={0}
              maxQuantity={editedProduct.boxes * editedProduct.unitsPerBox}
            />
          </CollapsibleSection>
        </div>

        {/* Save Button */}
        <div className="mt-6 flex justify-end">
          <Button onClick={handleSave} disabled={isSaving || editedProduct.photos.length === 0} className="min-w-32">
            <Save className="w-4 h-4 mr-2" />
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Modals */}
      {showScanner && (
        <BarcodeScanner
          onScanResult={handleScanResult}
          onClose={() => setShowScanner(false)}
        />
      )}


      {zoomedImage && (
        <ImageZoomModal
          imageUrl={zoomedImage}
          onClose={() => setZoomedImage(null)}
        />
      )}
    </div>
  );
};
